// PinInput.js
import React, { useRef, useEffect } from "react";
import styled from "styled-components";

const PinContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 0.6rem;
  margin: 1.2rem 0;
  font-family: "Inter", sans-serif;
`;

const PinBox = styled.input`
  width: 48px;
  height: 52px;
  text-align: center;
  font-size: 1.4rem;
  border-radius: 10px;
  border: 1px solid #d1d5db;
  outline: none;

  &:focus {
    border-color: #2563eb;
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }
`;

const PinInput = ({ length = 6, onChange, onComplete, defaultPin = "" }) => {
  const inputsRef = useRef([]);

  useEffect(() => {
    if (!defaultPin) return;

    const pin = String(defaultPin).slice(0, length);

    // populate DOM inputs (uncontrolled inputs) with values
    for (let i = 0; i < length; i++) {
      const el = inputsRef.current[i];
      if (el) el.value = pin[i] || "";
    }

    if (onChange) onChange(pin);
    if (pin.length === length && onComplete) {
      onComplete(pin);
      const last = inputsRef.current[length - 1];
      if (last) last.blur();
    }
  }, [defaultPin, length, onChange, onComplete]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (value.length === 1 && index < length - 1) {
      inputsRef.current[index + 1].focus();
    }

    const pin = inputsRef.current.map((input) => input.value).join("");
    onChange(pin);

    if (pin.length === length) {
      onComplete(pin);
      inputsRef.current[index].blur(); // closes keyboard
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      inputsRef.current[index - 1].focus();
    }

    if (e.key === "Enter") {
      // On mobile, "Next" triggers Enter
      // Move focus to next box if available
      if (index < length - 1) {
        inputsRef.current[index + 1].focus();
      } else {
        // If it's the last box, treat it as "done"
        const pin = inputsRef.current.map((input) => input.value).join("");
        onComplete(pin);
        inputsRef.current[index].blur(); // closes keyboard
      }
    }
  };

  return (
    <PinContainer>
      {Array.from({ length }).map((_, i) => (
        <PinBox
          key={i}
          type="tel"
          inputMode="numeric"
          maxLength={1}
          defaultValue={(defaultPin && defaultPin[i]) || ""}
          ref={(el) => (inputsRef.current[i] = el)}
          onChange={(e) => handleChange(e, i)}
          onKeyDown={(e) => handleKeyDown(e, i)}
        />
      ))}
    </PinContainer>
  );
};

export default PinInput;
